import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Alert,
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Chip,
  InputAdornment,
  Divider,
  Paper
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Search } from '@mui/icons-material';
import { studentService, Student } from '../services/studentService';
import assessmentService, { Assessment } from '../services/assessmentService';
import { toast } from 'react-toastify';

interface AssignSurveyDialogProps {
  open: boolean;
  onClose: () => void;
  assessment: Assessment;
  onSuccess: () => void;
}

const AssignSurveyDialog: React.FC<AssignSurveyDialogProps> = ({
  open,
  onClose,
  assessment,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedStudentIds, setSelectedStudentIds] = useState<number[]>([]);
  const [dueDate, setDueDate] = useState<Date | null>(null);
  const [notes, setNotes] = useState('');
  const [sendNotification, setSendNotification] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      loadStudents();
    }
  }, [open]);

  const loadStudents = async () => {
    try {
      setLoading(true);
      setError(null);
      // Load all students for the provider
      const response = await studentService.getStudents({ 
        page: 0, 
        size: 1000, // Get all students
        status: 'ACTIVE' 
      });
      setStudents(response.content || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load students');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStudent = (studentId: number) => {
    setSelectedStudentIds(prev => {
      if (prev.includes(studentId)) {
        return prev.filter(id => id !== studentId);
      } else {
        return [...prev, studentId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedStudentIds.length === filteredStudents.length) {
      setSelectedStudentIds([]);
    } else {
      setSelectedStudentIds(filteredStudents.map(s => s.id));
    }
  };

  const handleAssign = async () => {
    if (selectedStudentIds.length === 0) {
      setError('Please select at least one student');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      await assessmentService.assignAssessment(
        assessment.id,
        selectedStudentIds,
        dueDate?.toISOString(),
        notes,
        sendNotification
      );

      toast.success(`Assessment assigned to ${selectedStudentIds.length} student(s)`);
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Failed to assign assessment');
    } finally {
      setLoading(false);
    }
  };

  const filteredStudents = students.filter(student => {
    const searchLower = searchTerm.toLowerCase();
    return (
      student.firstName.toLowerCase().includes(searchLower) ||
      student.lastName.toLowerCase().includes(searchLower) ||
      student.email.toLowerCase().includes(searchLower) ||
      (student.studyProgram && student.studyProgram.toLowerCase().includes(searchLower))
    );
  });

  const isStudentSelected = (studentId: number) => selectedStudentIds.includes(studentId);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Assign Survey: {assessment.title}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* Search */}
          <TextField
            fullWidth
            placeholder="Search students by name, email, or program..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />

          {/* Student Selection */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1">
                Select Students ({selectedStudentIds.length} selected)
              </Typography>
              <Button
                size="small"
                onClick={handleSelectAll}
              >
                {selectedStudentIds.length === filteredStudents.length ? 'Deselect All' : 'Select All'}
              </Button>
            </Box>
            
            <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto' }}>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : filteredStudents.length === 0 ? (
                <Typography variant="body2" color="textSecondary" sx={{ p: 3, textAlign: 'center' }}>
                  No students found
                </Typography>
              ) : (
                <List>
                  {filteredStudents.map((student, index) => (
                    <React.Fragment key={student.id}>
                      <ListItem disablePadding>
                        <ListItemButton
                          onClick={() => handleToggleStudent(student.id)}
                          selected={isStudentSelected(student.id)}
                        >
                          <ListItemIcon>
                            <Checkbox
                              edge="start"
                              checked={isStudentSelected(student.id)}
                              tabIndex={-1}
                              disableRipple
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={`${student.firstName} ${student.lastName}`}
                            secondary={
                              <Box>
                                <Typography variant="caption" display="block">
                                  {student.email}
                                </Typography>
                                {student.studyProgram && (
                                  <Typography variant="caption" color="textSecondary">
                                    {student.studyProgram}
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                          {student.lastRiskLevel && (
                            <Chip
                              label={student.lastRiskLevel}
                              size="small"
                              color={
                                student.lastRiskLevel === 'RED' ? 'error' :
                                student.lastRiskLevel === 'YELLOW' ? 'warning' :
                                'success'
                              }
                            />
                          )}
                        </ListItemButton>
                      </ListItem>
                      {index < filteredStudents.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </Paper>
          </Box>

          {/* Due Date */}
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Due Date (Optional)"
              value={dueDate}
              onChange={(newValue) => setDueDate(newValue)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  sx: { mb: 2 }
                }
              }}
              minDate={new Date()}
            />
          </LocalizationProvider>

          {/* Notes */}
          <TextField
            fullWidth
            label="Notes (Optional)"
            multiline
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            sx={{ mb: 2 }}
          />

          {/* Send Notification */}
          <FormControlLabel
            control={
              <Checkbox
                checked={sendNotification}
                onChange={(e) => setSendNotification(e.target.checked)}
              />
            }
            label="Send email notification to students"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleAssign}
          disabled={loading || selectedStudentIds.length === 0}
        >
          {loading ? <CircularProgress size={20} /> : `Assign to ${selectedStudentIds.length} Student(s)`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignSurveyDialog;